export const RETAIL_POS_CONFIGURATION_VERSION =
  1;

export const RETAIL_POS_DOMAIN_ID =
  "retail";


/**
 * Immutable declarative Runtime configuration consumed
 * by the future Retail POS Domain Adapter.
 *
 * This contract deliberately contains no live business
 * state.
 */
export function createRetailPOSConfiguration(
  overrides = {}
) {
  if (!isPlainObject(overrides)) {
    throw new TypeError(
      "RetailPOSConfiguration input must be a plain object."
    );
  }

  const configuration = {
    version:
      normalizePositiveInteger(
        overrides.version,
        RETAIL_POS_CONFIGURATION_VERSION
      ),

    domainId:
      RETAIL_POS_DOMAIN_ID,

    publicationId:
      normalizeRequiredString(
        overrides.publicationId,
        "publicationId"
      ),

    companyUuid:
      normalizeRequiredString(
        overrides.companyUuid,
        "companyUuid"
      ),

    activityUuid:
      normalizeRequiredString(
        overrides.activityUuid,
        "activityUuid"
      ),

    publicationSchemaVersion:
      normalizePositiveInteger(
        overrides.publicationSchemaVersion,
        1
      ),

    runtimeVersion:
      normalizePositiveInteger(
        overrides.runtimeVersion,
        1
      ),

    catalog: {
      layout:
        cloneArray(
          overrides.catalog
            ?.layout
        ),

      categories:
        cloneArray(
          overrides.catalog
            ?.categories
        ),

      productShortcuts:
        cloneArray(
          overrides.catalog
            ?.productShortcuts
        ),
    },

    scannerBindings:
      clonePlainObject(
        overrides.scannerBindings
      ),

    terminalBindings:
      clonePlainObject(
        overrides.terminalBindings
      ),

    customerActions:
      cloneArray(
        overrides.customerActions
      ),

    loyaltyActions:
      cloneArray(
        overrides.loyaltyActions
      ),

    paymentMethods:
      cloneArray(
        overrides.paymentMethods
      ),

    receiptActions:
      cloneArray(
        overrides.receiptActions
      ),

    workspaceLayout: {
      workspaces:
        cloneArray(
          overrides.workspaceLayout
            ?.workspaces
        ),

      navigation:
        cloneArray(
          overrides.workspaceLayout
            ?.navigation
        ),

      layout:
        clonePlainObject(
          overrides.workspaceLayout
            ?.layout
        ),
    },

    themeReference:
      clonePlainObject(
        overrides.themeReference
      ),

    permissionsMetadata:
      clonePlainObject(
        overrides.permissionsMetadata
      ),
  };

  assertRetailPOSConfiguration(
    configuration
  );

  return deepFreeze(
    configuration
  );
}


/**
 * Validate without throwing.
 */
export function validateRetailPOSConfiguration(
  configuration
) {
  const issues = [];

  if (!isPlainObject(configuration)) {
    return {
      valid: false,

      issues: Object.freeze([
        createIssue(
          "$",
          "INVALID_CONFIGURATION",
          "RetailPOSConfiguration must be a plain object."
        ),
      ]),
    };
  }

  if (
    configuration.domainId
    !== RETAIL_POS_DOMAIN_ID
  ) {
    issues.push(
      createIssue(
        "domainId",
        "INVALID_DOMAIN_ID",
        'domainId must be "retail".'
      )
    );
  }

  validateRequiredString(
    configuration,
    "publicationId",
    issues
  );

  validateRequiredString(
    configuration,
    "companyUuid",
    issues
  );

  validateRequiredString(
    configuration,
    "activityUuid",
    issues
  );

  validatePositiveInteger(
    configuration,
    "version",
    issues
  );

  validatePositiveInteger(
    configuration,
    "publicationSchemaVersion",
    issues
  );

  validatePositiveInteger(
    configuration,
    "runtimeVersion",
    issues
  );

  if (
    !isPlainObject(
      configuration.catalog
    )
  ) {
    issues.push(
      createIssue(
        "catalog",
        "INVALID_CATALOG",
        "catalog must be a plain object."
      )
    );
  } else {
    validateArray(
      configuration.catalog,
      "layout",
      "catalog.layout",
      issues
    );

    validateArray(
      configuration.catalog,
      "categories",
      "catalog.categories",
      issues
    );

    validateArray(
      configuration.catalog,
      "productShortcuts",
      "catalog.productShortcuts",
      issues
    );
  }

  validatePlainObject(
    configuration,
    "scannerBindings",
    issues
  );

  validatePlainObject(
    configuration,
    "terminalBindings",
    issues
  );

  validateArray(
    configuration,
    "customerActions",
    "customerActions",
    issues
  );

  validateArray(
    configuration,
    "loyaltyActions",
    "loyaltyActions",
    issues
  );

  validateArray(
    configuration,
    "paymentMethods",
    "paymentMethods",
    issues
  );

  validateArray(
    configuration,
    "receiptActions",
    "receiptActions",
    issues
  );

  if (
    !isPlainObject(
      configuration.workspaceLayout
    )
  ) {
    issues.push(
      createIssue(
        "workspaceLayout",
        "INVALID_WORKSPACE_LAYOUT",
        "workspaceLayout must be a plain object."
      )
    );
  } else {
    validateArray(
      configuration.workspaceLayout,
      "workspaces",
      "workspaceLayout.workspaces",
      issues
    );

    validateArray(
      configuration.workspaceLayout,
      "navigation",
      "workspaceLayout.navigation",
      issues
    );

    validatePlainObject(
      configuration.workspaceLayout,
      "layout",
      issues,
      "workspaceLayout.layout"
    );
  }

  validatePlainObject(
    configuration,
    "themeReference",
    issues
  );

  validatePlainObject(
    configuration,
    "permissionsMetadata",
    issues
  );

  return {
    valid:
      issues.length === 0,

    issues:
      Object.freeze(issues),
  };
}


export function assertRetailPOSConfiguration(
  configuration
) {
  const validation =
    validateRetailPOSConfiguration(
      configuration
    );

  if (!validation.valid) {
    const error =
      new TypeError(
        "RetailPOSConfiguration validation failed."
      );

    error.issues =
      validation.issues;

    throw error;
  }

  return configuration;
}


export function isRetailPOSConfiguration(
  configuration
) {
  return validateRetailPOSConfiguration(
    configuration
  ).valid;
}


function validateRequiredString(
  target,
  field,
  issues
) {
  if (
    typeof target[field]
      !== "string"
    || target[field].trim()
      === ""
  ) {
    issues.push(
      createIssue(
        field,
        "INVALID_STRING",
        `${field} must be a non-empty string.`
      )
    );
  }
}


function validatePositiveInteger(
  target,
  field,
  issues
) {
  if (
    !Number.isInteger(
      target[field]
    )
    || target[field] < 1
  ) {
    issues.push(
      createIssue(
        field,
        "INVALID_INTEGER",
        `${field} must be a positive integer.`
      )
    );
  }
}


function validateArray(
  target,
  field,
  path,
  issues
) {
  if (
    !Array.isArray(
      target[field]
    )
  ) {
    issues.push(
      createIssue(
        path,
        "INVALID_ARRAY",
        `${path} must be an array.`
      )
    );
  }
}


function validatePlainObject(
  target,
  field,
  issues,
  path = field
) {
  if (
    !isPlainObject(
      target[field]
    )
  ) {
    issues.push(
      createIssue(
        path,
        "INVALID_OBJECT",
        `${path} must be a plain object.`
      )
    );
  }
}


function createIssue(
  path,
  code,
  message
) {
  return Object.freeze({
    path,
    code,
    message,
  });
}


function normalizeRequiredString(
  value,
  field
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    throw new TypeError(
      `RetailPOSConfiguration ${field} must be a non-empty string.`
    );
  }

  return value.trim();
}


function normalizePositiveInteger(
  value,
  fallback
) {
  if (
    value === undefined
    || value === null
  ) {
    return fallback;
  }

  if (
    !Number.isInteger(value)
    || value < 1
  ) {
    throw new TypeError(
      "RetailPOSConfiguration versions must be positive integers."
    );
  }

  return value;
}


function cloneArray(
  value
) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(
    deepClone
  );
}


function clonePlainObject(
  value
) {
  if (!isPlainObject(value)) {
    return {};
  }

  return deepClone(value);
}


function deepClone(
  value
) {
  if (Array.isArray(value)) {
    return value.map(
      deepClone
    );
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .map(
          ([key, entry]) => [
            key,
            deepClone(entry),
          ]
        )
    );
  }

  return value;
}


function deepFreeze(
  value
) {
  if (
    value === null
    || typeof value !== "object"
    || Object.isFrozen(value)
  ) {
    return value;
  }

  Object.freeze(value);

  Object.values(value)
    .forEach(
      deepFreeze
    );

  return value;
}


function isPlainObject(
  value
) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
  ) {
    return false;
  }

  const prototype =
    Object.getPrototypeOf(value);

  return (
    prototype === Object.prototype
    || prototype === null
  );
}
